import { useState, useEffect } from 'react';

export type SerializedFirebaseTimestamp = {
  _seconds: number;
  _nanoseconds: number;
};

/**
 * Custom hook that counts down to a specific Firebase timestamp
 * @param {SerializedFirebaseTimestamp} targetTimestamp - The Firebase timestamp to count down to
 * @returns {Object} countdown - Object containing days, hours, minutes, seconds
 */
export function useCountdownToTimestamp(targetTimestamp: SerializedFirebaseTimestamp | undefined) {
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!targetTimestamp) {
        return {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0
        }
      }

      // Get current date
      const now = new Date();
      
      // Convert SerializedFirebaseTimestamp to JavaScript Date
      // Firebase timestamps are in seconds, JavaScript dates use milliseconds
      const targetDate = new Date(targetTimestamp._seconds * 1000 + targetTimestamp._nanoseconds / 1000000);
      
      // Calculate the difference
      const difference = targetDate.getTime() - now.getTime();
      
      // Don't go below zero
      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }
      
      // Calculate days, hours, minutes, seconds
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    // Initial calculation
    setCountdown(calculateTimeLeft());
    
    // Set up interval to update countdown every second
    const timer = setInterval(() => {
      setCountdown(calculateTimeLeft());
    }, 1000);
    
    // Clean up interval on unmount
    return () => clearInterval(timer);
  }, [targetTimestamp?._seconds, targetTimestamp?._nanoseconds, targetTimestamp]);
  
  return countdown;
}